/**
 * The accessibility projection every field shell shares, independent of what the control is.
 *
 * Carries the state a screen reader needs — validity, requiredness, interactivity — and the
 * relations that tie the control to its label, description and error list. A widget that has no
 * controller of its own still needs all of it: without `aria-describedby` the error list is
 * rendered, styled, and announced to nobody.
 *
 * Deliberately narrower than a full field projection. `type`, `inputmode`, `autocomplete` and
 * `readonly` belong to a text control and are not projected here. Root classes are not projected
 * either: the shell already applies the kind's own, and restating them would create a second source
 * of truth.
 */
import { given } from "../given.js";
import { NO_CONSTRAINTS } from "@modyra/core";
import { nativeConstraintAttributes } from "../native-constraints.js";
import { defaultWidgetIdFactory as idFactory, assertUsableWidgetId } from "../ids.js";
import { MDY_FIELD_SHELL_CLASSES, MDY_FIELD_STATE_CLASSES } from "../structure.js";
import { nameIsAFallback, shownErrors } from "./verdict.js";
import { widgetSupportsState } from "../widget-states.js";
import { MDY_WIDGET_KINDS } from "../catalog/kinds.js";
/** The kinds whose control is a checkbox, and which therefore need a value of their own. */
const BOOLEAN_KINDS = new Set(["checkbox", "toggle"]);
/** The ids a shell's parts carry, so a renderer can put them on its own elements. */
export function fieldShellPartIds(widgetId) {
    assertUsableWidgetId(widgetId);
    return {
        labelId: idFactory.part(widgetId, "label"),
        descriptionId: idFactory.part(widgetId, "description"),
        errorId: idFactory.part(widgetId, "errors"),
    };
}
/**
 * The classes the field's own root carries, from the states it is in.
 *
 * Every kind had this function, and every copy was the same five lines over the same table. The
 * states a root may carry are declared once in `MDY_FIELD_STATE_CLASSES`; deriving the classes from
 * them is not a per-kind decision.
 */
export function fieldShellRootClasses(state) {
    const S = MDY_FIELD_STATE_CLASSES;
    return [
        S.field,
        ...S.fieldStates.filter((name) => Boolean(state[name])).map((name) => `${S.field}--${name}`),
    ];
}
/**
 * Whether this kind announces read-only at all.
 *
 * A kind this contract does not know is not this contract's to police: a consumer rendering their
 * own kind keeps what they had.
 */
function announcesReadonly(kind) {
    if (kind === undefined)
        return true;
    return MDY_WIDGET_KINDS.includes(kind)
        ? widgetSupportsState(kind, "readonly")
        : true;
}
export function projectFieldShellA11y(flags, errors, options) {
    const { labelId, descriptionId, errorId } = fieldShellPartIds(options.widgetId);
    // Out of play, no verdict — the wrapper, the label, `aria-invalid` and whether the error
    // text renders are four faces of one question, answered once in verdict.ts.
    const hasErrors = shownErrors(flags, errors).length > 0;
    // What the control describes itself by depends on what was *rendered*, not on what is wrong.
    const errorsVisible = options.errorsVisible ?? hasErrors;
    // Whether the control announces itself as failing is a different question from which element
    // carries the words. A field showing its errors *inline* renders no error list, so the one flag
    // answered "nothing rendered" and the control said it was valid — while the wrapper beside it
    // painted the refusal and an icon stated it. `aria-invalid` follows the verdict; `aria-describedby`
    // follows what exists to point at.
    const told = options.invalid ?? errorsVisible;
    const describedBy = fieldDescribedBy({
        errorId,
        descriptionId,
        // The error container is pointed at whenever it is on the page, which is not the same as
        // whenever it holds a message: a renderer that reserves it at rest keeps one stable reference
        // instead of writing one when a message arrives and withdrawing it when the message clears.
        errorsPresent: options.errorsReserved ?? errorsVisible,
        // A description exists when the renderer says one does. Defaulted to `true`, a control claimed
        // `aria-describedby` at rest and pointed it at an element holding nothing — which asserts that a
        // description exists and sends a reader to a text nobody wrote. It also makes "I have a
        // description, and it is empty" indistinguishable from "I have none", where silence is the
        // honest statement of nothing to say. The errors half of this reference was repaired for exactly
        // that reason; the hint half is the same shape.
        descriptionPresent: options.supportingText === true || (options.supportingText ?? "") !== "",
    });
    return {
        label: {
            id: labelId,
            classes: [
                MDY_FIELD_SHELL_CLASSES.label,
                ...(options.nameSources !== undefined && nameIsAFallback(options.nameSources)
                    ? [`${MDY_FIELD_SHELL_CLASSES.label}--unwritten`]
                    : []),
            ],
            attributes: options.controlId ? { for: options.controlId } : {},
        },
        control: {
            classes: [],
            attributes: {
                // What a native submit reads. Every renderer that binds this part gets it, which is the
                // point of it living here: three renderers writing the same attribute is three places for
                // one of them to forget.
                name: options.submitName ?? null,
                // A checked box with no `value` sends the string `on`, which describes the box rather than
                // the answer. The kinds whose control is a checkbox say what they mean instead.
                value: options.submitName !== undefined && BOOLEAN_KINDS.has(options.kind ?? "") ? "true" : null,
                // What the field's rules state, in the attributes this kind's control can carry. Absent
                // members are `null`, which is how a part contract says "remove this".
                ...nativeConstraintAttributes(options.kind ?? "text", options.constraints ?? NO_CONSTRAINTS, options.value ?? null),
                // What is *shown*, not what is wrong. The four faces of one question include this one: a
                // control marked wrong beside a message nobody rendered is a verdict with no explanation,
                // and the person it is about did nothing to earn it.
                "aria-invalid": String(told),
                "aria-required": String(flags.required),
                // Disabled alone, never folded with read-only: a read-only control is reachable, and
                // announcing it disabled tells a screen-reader user they cannot interact with something
                // they can.
                "aria-disabled": String(flags.disabled),
                // And the native refusal beside the announced one. `aria-disabled` says a control cannot be
                // used; only `disabled` makes that true. A kind that carried the first without the second
                // announced a refusal it did not enforce — the control stayed operable, and a press changed
                // a value the model refuses. Two kinds reached their renderers that way, because the
                // projections that add it themselves are the ones that happened to.
                disabled: flags.disabled,
                // Read-only in its own word, on the kinds that declare the state. A control that refuses
                // every change while staying focusable and submitted looks identical to one that does not;
                // `file` is the kind that declares no read-only — its picker is the browser's and its role
                // has no attribute to carry this.
                "aria-readonly": flags.readonly && announcesReadonly(options.kind) ? "true" : null,
                // Names the error list only while it is rendered; otherwise the description, if there is one.
                "aria-describedby": describedBy,
            },
        },
        description: {
            id: descriptionId,
            classes: [MDY_FIELD_SHELL_CLASSES.supportingText],
            attributes: {},
            ...(typeof options.supportingText === "string" && options.supportingText !== ""
                ? { content: { text: options.supportingText } } : {}),
        },
        error: {
            id: errorId,
            classes: [MDY_FIELD_SHELL_CLASSES.errors],
            attributes: {
                // A live region and nothing more. `role="alert"` here would override the list semantics of
                // the <ul> it sits on: axe reports every <li> inside such a list as an orphaned list item, and
                // a screen reader sees the same thing. `aria-live` announces the list when it appears, so the
                // role would cost the structure and add nothing.
                "aria-live": "polite",
            },
        },
        requiredMarker: {
            classes: [MDY_FIELD_SHELL_CLASSES.requiredMarker],
            attributes: { "aria-hidden": "true" },
        },
    };
}
/**
 * What describes a control: its error, then its help, both when both are there.
 *
 * Not one or the other. An error message does not take the place of the instruction that would have
 * prevented it — losing the help at the moment it is most useful — and a description is a list, so
 * both fit. The error goes first because it is the new thing, and somebody who moves on after the
 * first sentence has heard the one that mattered.
 *
 * An element with no text contributes nothing to the description: it is not read as a pause or as
 * "empty", it is as though the reference were absent, until text appears inside it. That is what
 * makes a permanently-present reference cheaper than a correct one.
 */
export function fieldDescribedBy(parts) {
    given("fieldDescribedBy", "{ errorId, descriptionId, errorsPresent, descriptionPresent }", parts, ["errorId", "descriptionId", "errorsPresent", "descriptionPresent"]);
    const named = [
        parts.errorsPresent ? parts.errorId : null,
        parts.descriptionPresent ? parts.descriptionId : null,
    ].filter((id) => id !== null);
    return named.length > 0 ? named.join(" ") : null;
}
